import { makeStyles } from "@material-ui/core/styles";
import { useCallback, useState } from "react";
import { Checkbox, FormControlLabel, Radio, RadioGroup } from "@material-ui/core";
import {
  RADAR_TYPE_ASIA,
  RADAR_TYPE_AU,
  RADAR_TYPE_CA, RADAR_TYPE_EU,
  RADAR_TYPE_JP,
  RADAR_TYPE_KR,
  RADAR_TYPE_TW
} from "../layers/Radar";
import { useMapColorState } from "../../../store/MapColorState";
import { weatherTimeWithID, WX_ID_RADAR } from "../../../store/WeatherContentsState";
import { useRecoilValue } from "recoil";

const useStyles = makeStyles(_ => ({
  box: {
    display: 'flex',
    flexDirection:'row',
    flexWrap:'wrap',
    justifyContent: 'flex-start'
  },
  inputLabel: {
    fontSize:"12px"
  },
  rdo: {
    paddingTop: '0px',
    paddingBottom: '0px',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  radarLegend: {
    height: "80px",
    padding: "0",
    display: "flex",
    flexDirection: "column",
    flexWrap: "wrap",
    '&>li': {
      position: 'relative',
      lineHeight: '30px',
      height: '20px',
      paddingLeft: '30px',
      textAlign: 'left'
    }
  },
  radarGt80:{'&:after': {background: 'rgba(255,0,255, 0.588)'}},
  radarLte80:{'&:after': {background: 'rgba(244,0,161, 0.588)'}},
  radarLte64:{'&:after': {background: 'rgba(255,0,0, 0.588)'}},
  radarLte56:{'&:after': {background: 'rgba(255,21,6, 0.588)'}},
  radarLte48:{'&:after': {background: 'rgba(255,140,0, 0.588)'}},
  radarLte40:{'&:after': {background: 'rgba(255,165,0, 0.588)'}},
  radarLte32:{'&:after': {background: 'rgba(255,191,0, 0.588)'}},
  radarLte24:{'&:after': {background: 'rgba(255,255,0, 0.588)'}},
  radarLte16:{'&:after': {background: 'rgba(135,255,6, 0.588)'}},
  radarLte12:{'&:after': {background: 'rgba(0,255,0, 0.588)'}},
  radarLte8:{'&:after': {background: 'rgba(6,207,34, 0.588)'}},
  radarLte4:{'&:after': {background: 'rgba(0,0,255, 0.588)'}},
  radarLte2:{'&:after': {background: 'rgba(30,144,255, 0.588)'}},
  radarLte1:{'&:after': {background: 'rgba(0,255,255, 0.588)'}},
}));

function LegendRadar({contents: radar}) {
  const classes = useStyles();
  const timeInfo = useRecoilValue(weatherTimeWithID(WX_ID_RADAR));
  const times = [].concat(timeInfo.baseTime);
  const {mapColor} = useMapColorState();
  const [dataType, setDataType] = useState(radar.getDataType());
  const [coverage, setCoverage] = useState(radar.getCoverage());


  const handleDataTypeChange = useCallback(event => {
    radar.setDataType(event.target.value);
    setDataType(event.target.value);
  }, [radar]);

  const handleCoverageChange = useCallback(event => {
    radar.setCoverage(event.target.checked);
    setCoverage(event.target.checked);
  },[radar]);


  return (
    <div className={mapColor}>
      {times.map((time, idx)=> {
        return(
          <div key={idx} className="legend-contents-time">
            {time}
          </div>
        )
      })}
      <div>
        <RadioGroup row aria-label="position" name="rdo-data-type" value={dataType}>
          <FormControlLabel value={RADAR_TYPE_JP} control={<Radio className={classes.rdo} color="primary" size="small" disableRipple={true}/>} label={<span className={classes.inputLabel}>Japan</span>} onChange={handleDataTypeChange}/>
          <FormControlLabel value={RADAR_TYPE_KR} control={<Radio className={classes.rdo} color="primary" size="small"/>} label={<span className={classes.inputLabel}>Korea</span>} onChange={handleDataTypeChange}/>
          <FormControlLabel value={RADAR_TYPE_TW} control={<Radio className={classes.rdo} color="primary" size="small"/>} label={<span className={classes.inputLabel}>Taiwan</span>} onChange={handleDataTypeChange}/>
          <FormControlLabel value={RADAR_TYPE_ASIA} control={<Radio className={classes.rdo} color="primary" size="small"/>} label={<span className={classes.inputLabel}>Japan/Korea/Taiwan</span>} onChange={handleDataTypeChange}/>
          <FormControlLabel value={RADAR_TYPE_AU} control={<Radio className={classes.rdo} color="primary" size="small"/>} label={<span className={classes.inputLabel}>Australia</span>} onChange={handleDataTypeChange}/>
          <FormControlLabel value={RADAR_TYPE_CA} control={<Radio className={classes.rdo} color="primary" size="small"/>} label={<span className={classes.inputLabel}>US/Canada</span>} onChange={handleDataTypeChange}/>

          {/* TODO USレーダ停止解除までコメントアウト <FormControlLabel value={RADAR_TYPE_US} control={<Radio className={classes.rdo} color="primary" size="small"/>} label={<span className={classes.inputLabel}>US</span>} onChange={handleDataTypeChange}/>*/}
          <FormControlLabel value={RADAR_TYPE_EU} control={<Radio className={classes.rdo} color="primary" size="small"/>} label={<span className={classes.inputLabel}>EU</span>} onChange={handleDataTypeChange}/>
          <FormControlLabel
            control={<Checkbox checked={coverage} onChange={handleCoverageChange} className={classes.rdo} size='small'/>}
            label={<span className={classes.inputLabel}>Coverage</span>}
          />
        </RadioGroup>
      </div>
      <ul className={classes.radarLegend}>
        <li className={'tone ' + classes.radarGt80}><span className="tone-separator">&gt; </span><span className="tone-val">80mm</span></li>
        <li className={'tone ' + classes.radarLte80}><span className="tone-separator">&lt;=</span><span className="tone-val">80mm</span></li>
        <li className={'tone ' + classes.radarLte64}><span className="tone-separator">&lt;=</span><span className="tone-val">64mm</span></li>
        <li className={'tone ' + classes.radarLte56}><span className="tone-separator">&lt;=</span><span className="tone-val">56mm</span></li>
        <li className={'tone ' + classes.radarLte48}><span className="tone-separator">&lt;=</span><span className="tone-val">48mm</span></li>
        <li className={'tone ' + classes.radarLte40}><span className="tone-separator">&lt;=</span><span className="tone-val">40mm</span></li>
        <li className={'tone ' + classes.radarLte32}><span className="tone-separator">&lt;=</span><span className="tone-val">32mm</span></li>
        <li className={'tone ' + classes.radarLte24}><span className="tone-separator">&lt;=</span><span className="tone-val">24mm</span></li>
        <li className={'tone ' + classes.radarLte16}><span className="tone-separator">&lt;=</span><span className="tone-val">16mm</span></li>
        <li className={'tone ' + classes.radarLte12}><span className="tone-separator">&lt;=</span><span className="tone-val">12mm</span></li>
        <li className={'tone ' + classes.radarLte8}><span className="tone-separator">&lt;=</span><span className="tone-val">8mm</span></li>
        <li className={'tone ' + classes.radarLte4}><span className="tone-separator">&lt;=</span><span className="tone-val">4mm</span></li>
        <li className={'tone ' + classes.radarLte2}><span className="tone-separator">&lt;=</span><span className="tone-val">2mm</span></li>
        <li className={'tone ' + classes.radarLte1}><span className="tone-separator">&lt;=</span><span className="tone-val">1mm</span></li>
      </ul>
    </div>
  )
}

export default LegendRadar;